import { useState } from 'react'
import PropTypes from 'prop-types'

/**
 * ChecklistReviewItem Component
 *
 * Individual checklist item in PM review modal
 *
 * @param {Object} item - Checklist item
 * @param {number} index - Item index
 * @param {Object} decision - Current decision for this item
 * @param {Function} onAccept - Accept callback
 * @param {Function} onExcuse - Excuse callback (receives reason)
 * @param {boolean} disabled - Disabled state
 */
const ChecklistReviewItem = ({ item, index, decision, onAccept, onExcuse, disabled }) => {
  const [showExcuseInput, setShowExcuseInput] = useState(false)
  const [excuseReason, setExcuseReason] = useState('')

  const handleAcceptClick = () => {
    setShowExcuseInput(false)
    setExcuseReason('')
    onAccept()
  }

  const handleExcuseClick = () => {
    if (!showExcuseInput) {
      setShowExcuseInput(true)
    } else if (excuseReason.trim()) {
      onExcuse(excuseReason)
    }
  }

  const isAccepted = decision?.action === 'accept'
  const isExcused = decision?.action === 'excuse'

  return (
    <div
      className={`checklist-review-item ${isAccepted ? 'checklist-review-item--accepted' : ''} ${isExcused ? 'checklist-review-item--excused' : ''}`}
    >
      <div className="checklist-review-item__header">
        <div className="checklist-review-item__index">{index + 1}</div>
        <div className="checklist-review-item__content">
          <h4 className="checklist-review-item__title">{item.title}</h4>
          {item.description && (
            <p className="checklist-review-item__description">{item.description}</p>
          )}
          {item.control_reference && (
            <span className="checklist-review-item__reference">{item.control_reference}</span>
          )}
        </div>
      </div>

      <div className="checklist-review-item__actions">
        <button
          className={`btn btn--sm ${isAccepted ? 'btn--success' : 'btn--outline-success'}`}
          onClick={handleAcceptClick}
          disabled={disabled}
        >
          {isAccepted ? '✓ Accepted' : '✓ Accept'}
        </button>

        <button
          className={`btn btn--sm ${isExcused ? 'btn--danger' : 'btn--outline-danger'}`}
          onClick={handleExcuseClick}
          disabled={disabled}
        >
          {isExcused ? '✗ Excused' : '✗ Excuse'}
        </button>
      </div>

      {(showExcuseInput || isExcused) && (
        <div className="checklist-review-item__excuse">
          <label className="checklist-review-item__excuse-label">Excuse Reason:</label>
          <textarea
            className="checklist-review-item__excuse-input"
            value={isExcused ? decision.reason : excuseReason}
            onChange={(e) => setExcuseReason(e.target.value)}
            placeholder="Explain why this item is being excused..."
            rows={3}
            disabled={disabled || isExcused}
          />
          {!isExcused && (
            <button
              className="btn btn--sm btn--danger"
              onClick={handleExcuseClick}
              disabled={!excuseReason.trim() || disabled}
            >
              Confirm Excuse
            </button>
          )}
        </div>
      )}
    </div>
  )
}

ChecklistReviewItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  decision: PropTypes.object,
  onAccept: PropTypes.func.isRequired,
  onExcuse: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default ChecklistReviewItem
